import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Box, Mail, ArrowLeft, Copy, Check } from 'lucide-react'
import { authApi } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { readIntendedPlan } from '../../lib/intendedPlan'

/**
 * Email-OTP verification step.
 *
 * Reachable from two paths:
 *   1. Register success → router state carries { email, devOtp? }
 *   2. Login 403 (unverified account) → router state carries { email, devOtp? }
 *
 * On success the server hands us a sanctum token, which we drop into
 * localStorage exactly the way AuthContext.login does, then redirect
 * the user into the operator app.
 */
export default function VerifyOtp() {
  const navigate = useNavigate()
  const location = useLocation()
  const { updateUser } = useAuth()

  const stateEmail  = location.state?.email
  const initialDev  = location.state?.devOtp ?? null

  // Belt and suspenders: if a user refreshes the page we lose router
  // state, so we also stash the email in sessionStorage on first mount.
  const email = stateEmail || sessionStorage.getItem('pending_otp_email') || ''

  useEffect(() => {
    if (stateEmail) sessionStorage.setItem('pending_otp_email', stateEmail)
  }, [stateEmail])

  // If we landed here with no email at all, send the user back to login.
  useEffect(() => {
    if (!email) navigate('/login', { replace: true })
  }, [email, navigate])

  const [digits, setDigits]   = useState(['', '', '', '', '', ''])
  const [devOtp, setDevOtp]   = useState(initialDev)
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)
  const [error, setError]     = useState('')
  const [info, setInfo]       = useState('')
  const [copied, setCopied]   = useState(false)

  const inputs = useRef([])

  const focusIndex = (i) => {
    const el = inputs.current[i]
    if (el) el.focus()
  }

  const setDigit = (i, value) => {
    // Allow paste of full code into any box.
    if (value.length > 1) {
      const chars = value.replace(/\D/g, '').slice(0, 6).split('')
      const next  = ['', '', '', '', '', '']
      chars.forEach((c, idx) => { next[idx] = c })
      setDigits(next)
      focusIndex(Math.min(chars.length, 5))
      return
    }
    if (value && !/^\d$/.test(value)) return
    setDigits(prev => {
      const next = [...prev]
      next[i] = value
      return next
    })
    if (value && i < 5) focusIndex(i + 1)
  }

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      focusIndex(i - 1)
    }
    if (e.key === 'ArrowLeft' && i > 0)  focusIndex(i - 1)
    if (e.key === 'ArrowRight' && i < 5) focusIndex(i + 1)
  }

  const fillFromDev = () => {
    if (!devOtp) return
    setDigits(String(devOtp).split(''))
    focusIndex(5)
    setError('')
  }

  const copyDev = async () => {
    if (!devOtp) return
    try {
      await navigator.clipboard.writeText(String(devOtp))
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // No clipboard permission - silently ignore.
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const code = digits.join('')
    if (code.length !== 6) {
      setError('Enter the 6-digit code from your email.')
      return
    }

    setError('')
    setInfo('')
    setLoading(true)
    try {
      const { data } = await authApi.verifyEmailOtp({ email, code })

      if (data.already_verified) {
        setInfo('Already verified - sign in to continue.')
        sessionStorage.removeItem('pending_otp_email')
        setTimeout(() => navigate('/login', { replace: true }), 900)
        return
      }

      // Mirror AuthContext.login: persist token + user, push into context.
      localStorage.setItem('token', data.token)
      localStorage.setItem('user',  JSON.stringify(data.user))
      sessionStorage.removeItem('pending_otp_email')
      updateUser(data.user)

      // Someone who signed up from a paid pricing CTA gets taken to the
      // plan screen to finish the purchase; everyone else starts at
      // facilities, which is the first thing a new operator has to set up.
      const intended = readIntendedPlan()
      navigate(intended ? `/settings?plan=${intended}` : '/facilities', { replace: true })
    } catch (err) {
      setError(
        err.response?.data?.errors?.code?.[0] ||
        err.response?.data?.message ||
        'Could not verify that code.'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    if (!email) return
    setError('')
    setInfo('')
    setResending(true)
    try {
      const { data } = await authApi.resendEmailOtp({ email })
      setInfo('A new code is on its way.')
      if (data?.dev_otp) {
        setDevOtp(data.dev_otp)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Could not resend the code.')
    } finally {
      setResending(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2.5 justify-center mb-8 hover:opacity-80 transition-opacity" aria-label="Go to Stovalt home">
          <div className="w-9 h-9 bg-brand-500 rounded-xl flex items-center justify-center">
            <Box size={18} className="text-white" />
          </div>
          <span className="font-display font-700 text-white text-xl">Stovalt</span>
        </Link>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
          <div className="w-10 h-10 bg-brand-500/10 rounded-xl flex items-center justify-center mb-4">
            <Mail size={20} className="text-brand-400" />
          </div>
          <h1 className="font-display text-xl font-700 text-white mb-1">Verify your email</h1>
          <p className="text-slate-400 text-sm mb-6">
            We sent a 6-digit code to{' '}
            <span className="text-slate-200 font-medium">{email}</span>.
            It expires in 10 minutes.
          </p>

          {devOtp && (
            <div className="mb-6 p-4 rounded-xl border border-amber-500/30 bg-amber-500/10">
              <p className="text-xs font-semibold uppercase tracking-wider text-amber-300 mb-2">
                🛠️ Dev mode - email not sent
              </p>
              <button
                type="button"
                onClick={copyDev}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-slate-950/40 border border-amber-500/20 text-amber-100 font-mono text-lg tracking-[0.4em] hover:bg-slate-950/60 transition"
                title="Click to copy"
              >
                <span>{devOtp}</span>
                {copied
                  ? <Check size={16} className="text-emerald-400" />
                  : <Copy size={14} className="text-amber-300/70" />
                }
              </button>
              <button
                type="button"
                onClick={fillFromDev}
                className="mt-2 w-full py-2 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 text-xs font-semibold transition"
              >
                Use this code
              </button>
              <p className="text-[11px] text-amber-200/70 mt-2 leading-relaxed">
                Stage and production environments still email this code normally.
              </p>
            </div>
          )}

          {error && (
            <div className="mb-4 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
              {error}
            </div>
          )}
          {info && (
            <div className="mb-4 px-4 py-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-sm text-emerald-400">
              {info}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Verification code
              </label>
              <div className="flex gap-2 justify-between">
                {digits.map((d, i) => (
                  <input
                    key={i}
                    ref={el => (inputs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={i === 0 ? 6 : 1}
                    value={d}
                    onChange={(e) => setDigit(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    autoFocus={i === 0}
                    className="w-full aspect-square text-center text-xl font-display font-700 text-white bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition"
                  />
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading
                ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : 'Verify and continue'
              }
            </button>
          </form>

          <div className="mt-6 flex items-center justify-between text-sm">
            <Link
              to="/login"
              className="flex items-center gap-1.5 text-slate-500 hover:text-slate-300"
            >
              <ArrowLeft size={14} /> Back to sign in
            </Link>
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="text-brand-400 hover:text-brand-300 font-medium disabled:opacity-50"
            >
              {resending ? 'Sending…' : 'Resend code'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
